'use client'

import { QUERY_REFETCH_INTERVALS, QUERY_STALE_TIMES } from '@/constants/query'
import { getWalletProvider } from '@/lib/wallets/registry'
import type { BridgePlatform, MarketBalance, WalletRuntime } from '@/lib/wallets/types'
import { useWallet } from '@/providers/WalletContext'
import { useQuery } from '@tanstack/react-query'

export interface UseMarketBalanceOptions {
	runtime?: Partial<WalletRuntime>
	enabled?: boolean
}

export function useMarketBalance(platform: BridgePlatform, options: UseMarketBalanceOptions = {}) {
	const wallet = useWallet()
	const provider = getWalletProvider(platform)
	const runtime: WalletRuntime = {
		eoaAddress: options.runtime?.eoaAddress ?? wallet.eoaAddress,
		solanaAddress: options.runtime?.solanaAddress ?? wallet.solanaAddress,
		addressOverrides: options.runtime?.addressOverrides,
		publicClient: options.runtime?.publicClient ?? wallet.publicClient,
		basePublicClient: options.runtime?.basePublicClient ?? wallet.basePublicClient,
		solanaConnection: options.runtime?.solanaConnection ?? wallet.solanaConnection,
		polymarketSafe: options.runtime?.polymarketSafe ?? wallet.polymarketSafe,
		polymarketActiveWallet:
			options.runtime?.polymarketActiveWallet ?? wallet.polymarketActiveWallet,
	}
	const address = provider.resolveAddress(runtime)

	const query = useQuery<MarketBalance | null>({
		queryKey: provider.balanceQueryKey(address),
		queryFn: () => provider.readBalance(runtime),
		enabled: (options.enabled ?? true) && !!address,
		staleTime: QUERY_STALE_TIMES.BALANCE,
		refetchInterval: QUERY_REFETCH_INTERVALS.BALANCE,
		refetchIntervalInBackground: true,
		refetchOnWindowFocus: true,
	})

	return {
		...query,
		platform,
		address,
		balance: query.data ?? null,
		cash: query.data?.cash ?? null,
	}
}
